import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from './api.service';
import { UserNotification } from '../models/notification.model';

@Injectable({ providedIn: 'root' })
export class ApiNotificationsService {
  private api = inject(ApiService);

  getUserNotifications(limit?: number, offset?: number): Observable<{
    notifications: UserNotification[];
    total: number;
    limit: number;
    offset: number;
  }> {
    return this.api.get<{
      notifications: UserNotification[];
      total: number;
      limit: number;
      offset: number;
    }>('notifications', { limit, offset }).pipe(
      map(result => ({
        ...result,
        notifications: result.notifications.map(n => this.transformNotification(n)),
      })),
    );
  }

  // Admin methods
  getAllNotifications(
    limit?: number,
    offset?: number,
    params?: { serviceZoneId?: string },
  ): Observable<{
    notifications: UserNotification[];
    total: number;
    limit: number;
    offset: number;
  }> {
    return this.api.get<{
      notifications: UserNotification[];
      total: number;
      limit: number;
      offset: number;
    }>('notifications/admin', { limit, offset, ...params }).pipe(
      map(result => ({
        ...result,
        notifications: result.notifications.map(n => this.transformNotification(n)),
      })),
    );
  }

  createNotification(notification: Omit<UserNotification, 'id' | 'createdAt'>): Observable<UserNotification> {
    return this.api.post<UserNotification>('notifications', notification).pipe(
      map(n => this.transformNotification(n)),
    );
  }

  sendBulkNotification(userIds: string[], title: string, body: string, data?: any, imageUrl?: string): Observable<any> {
    return this.api.post<any>('notifications/bulk', {
      userIds,
      title,
      body,
      data: data || {},
      imageUrl,
    });
  }

  sendToRole(role: string, title: string, body: string, data?: any, imageUrl?: string): Observable<any> {
    return this.api.post<any>('notifications/role', {
      role,
      title,
      body,
      data: data || {},
      imageUrl,
    });
  }

  updateNotification(id: string, notification: Partial<UserNotification>): Observable<UserNotification> {
    return this.api.put<UserNotification>(`notifications/${id}`, notification).pipe(
      map(n => this.transformNotification(n)),
    );
  }

  deleteNotification(id: string): Observable<void> {
    return this.api.delete<void>(`notifications/${id}`);
  }

  markAsRead(notificationId: string): Observable<any> {
    return this.api.post<any>(`notifications/${notificationId}/read`, {});
  }

  registerFCMToken(token: string): Observable<any> {
    return this.api.post<any>('notifications/register-token', { token });
  }

  unregisterFCMToken(token: string): Observable<any> {
    return this.api.post<any>('notifications/unregister-token', { token });
  }

  private transformNotification(notification: any): UserNotification {
    return {
      ...notification,
      createdAt: this.dateToTimestamp(notification.createdAt),
    };
  }

  private dateToTimestamp(date: Date | string | number): number {
    if (typeof date === 'number') return date;
    if (typeof date === 'string') return new Date(date).getTime();
    return date.getTime();
  }
}

